import {stringify} from 'query-string';

const settings = window.settings || {};
const hosts = settings.hosts || {};
const API_URL = hosts.api || '';

// Prefix URL, if needed.
// For example, links returned by API links are already absolute
function prefixUrl(url) {
  // If the url passed is already prefixed with the api host, return the
  // Url, otherwise prefix it with the api host.
  const apiPrefixed = url.indexOf(API_URL) === 0;
  if (apiPrefixed || !API_URL) {
    return url;
  }
  // fix double slash: https://host//path -> https://host/path
  const hostSlash = API_URL[API_URL.length - 1] === '/';
  const pathSlash = url[0] === '/';
  if (hostSlash && pathSlash) {
    return API_URL + url.substring(1, url.length);
  }
  return API_URL + url;
}

export const patch = (url, payload, prefix = true) => {
  const requestUrl = prefix ? prefixUrl(url) : url;
  return fetch(requestUrl, {
    body: JSON.stringify(payload),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': settings.csrf,
      Prefer: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    },
    method: 'PATCH',
  })
    .then((response) => response.json())
    .catch((error) => error.message);
};

// Note that django uses request.body for JSON
// and request.POST for form data.
export const post = (url, payload, prefix = true, headers = null) => {
  const requestUrl = prefix ? prefixUrl(url) : url;
  const isFormData = payload instanceof FormData;
  const body = isFormData ? payload : JSON.stringify(payload);
  const contentType = isFormData ? {} : {'Content-Type': 'application/json'};
  return fetch(requestUrl, {
    body,
    credentials: 'include',
    headers: {
      'X-CSRFToken': settings.csrf,
      Prefer: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
      ...contentType,
      ...(headers || {}),
    },
    method: 'POST',
  }).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  });
};

export const put = (url, payload, prefix = true) => {
  const requestUrl = prefix ? prefixUrl(url) : url;
  return fetch(requestUrl, {
    body: JSON.stringify(payload),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': settings.csrf,
      Prefer: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    },
    method: 'PUT',
  }).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  });
};

export const get = (url, query, prefix = true, headers = null) => {
  let requestUrl = prefix ? prefixUrl(url) : url;
  if (query && Object.keys(query).length) {
    requestUrl = `${requestUrl}?${stringify(query)}`;
  }
  return fetch(requestUrl, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Prefer: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
      ...(headers || {}),
    },
  })
    .then((response) => response.json())
    .catch((error) => error.message);
};

export const _delete = (url, prefix = true) => {
  const requestUrl = prefix ? prefixUrl(url) : url;
  return fetch(requestUrl, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': settings.csrf,
      Prefer: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    },
    method: 'DELETE',
  })
    .then((response) => response.json())
    .catch((error) => error.message);
};

export const me = () => {
  return get('/me');
};

export const users = () => {
  return get('/users');
};

export const meters = () => {
  return get('/meters');
};

export const meter = (uuid) => {
  return get(`/meters/${uuid}`);
};

export const meterData = (uuid, resolution, start, end) => {
  const query = {
    resolution,
    start,
    end,
  };
  return get(`/meters/${uuid}/data`, query);
};

export const definitions = () => {
  return get('/definitions');
};

export const buildings = (parentId, perPage, fields, enabled) => {
  const params = {parent: parentId, perPage, enabled};
  if (fields) {
    params.fields = fields.join(',');
  }
  return get('/buildings', params);
};

export const notifications = (uuid) => {
  return get('/notifications', uuid ? {meter: uuid} : null);
};

export const organizationPrefs = (orgId) => {
  return get(`organizations/${orgId}/settings`);
};

export const updateOrganizationPrefs = (orgId, prefs) => {
  return patch(`organizations/${orgId}/settings`, prefs);
};

export const building = (buildingId) => {
  return get(`buildings/${buildingId}`);
};

// Fetch meta data about tenant (for info card on dashboard)
export const tenant = (tenantId) => {
  return get(`tenants/${tenantId}`);
};

// Given buildingId return building reports, else org reports
export const reports = (buildingId, isTenant = false) => {
  const urlVarname = isTenant ? 'tenant' : 'building';
  const getParams = buildingId ? `?${urlVarname}=${buildingId}` : '';
  return get(`/reports${getParams}`);
};

export const assetReports = (assetId) => {
  const getParams = `?asset=${assetId}`;
  return get(`/reports${getParams}`);
};

export const reportsDashboard = (dashboardId) => {
  return get(`/reports?dashboard=${dashboardId}`);
};

export const reportsDashboardByChapter = (chapterId) => {
  return get(`/reports?chapter=${chapterId}`);
};

export const updateReportConfiguration = (reportId, config) => {
  return patch(`reports/${reportId}`, config);
};
